import { useNavigate } from 'react-router-dom';

import { useRecentlyViewedEmployeesGet } from '@sbv-clients/data-access-api';
import {
  Badge,
  Box,
  Card,
  CircularProgress,
  Divider,
  Icon,
  IconButton,
  LetterAvatar,
  Stack,
  Typography,
  useTheme,
} from '@sbv-clients/ui-shared';

import { useCardSx } from './components/use-card-sx';
import { ViewAllButton } from './components/view-all-button';

const EmployeeListRow = ({
  name,
  pictureUrl,
  isActive,
  onClick,
}: {
  name: string;
  pictureUrl: string;
  isActive: boolean;
  onClick: () => void;
}) => {
  const theme = useTheme();
  const inactive = !isActive;

  return (
    <Box
      display="flex"
      alignItems="center"
      gap={2}
      py={3}
      onClick={onClick}
      sx={{ '&:hover': { bgcolor: 'grey.50' }, cursor: 'pointer' }}
    >
      <Badge
        overlap="circular"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        variant="dot"
        sx={{
          '& .MuiBadge-badge': {
            right: 4,
            bottom: 5,
            backgroundColor: inactive
              ? theme.background.errorDot
              : theme.colors.success.main,
            border: '1px solid white',
            height: '10px',
            width: '10px',
            borderRadius: 360,
          },
        }}
      >
        <LetterAvatar
          inactive={inactive}
          name={name}
          src={pictureUrl}
          size="sm"
          sx={{
            height: 40,
            width: 40,
            flexShrink: 0,
            opacity: inactive ? 0.5 : 1,
            background: inactive
              ? theme.background.lightGrey
              : theme.background.lightPurple,
            color: inactive
              ? theme.background.darkGrey2
              : theme.colors.primary.main,
            cursor: 'pointer',
          }}
        />
      </Badge>
      <Typography
        variant="body2"
        fontWeight={500}
        flex={1}
        letterSpacing={0.5}
        sx={{
          color: inactive ? theme.background.darkGrey2 : theme.text.darkText,
        }}
      >
        {name}
      </Typography>
      <IconButton size="small" sx={{ color: 'primary.main' }}>
        <Icon name="ChevronRight" customSizes={{ height: 20, width: 20 }} />
      </IconButton>
    </Box>
  );
};

export const RecentlyViewedCard = () => {
  const cardSx = useCardSx();
  const navigate = useNavigate();
  const { data: recentEmployees = [], isLoading } =
    useRecentlyViewedEmployeesGet();

  return (
    <Card sx={{ ...cardSx, paddingBottom: 0 }} elevation={0}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="overline" color="grey.500" fontWeight={700}>
          Recently Viewed Employees
        </Typography>
        <ViewAllButton
          onClick={() =>
            navigate('/employees', { state: { skipPersistedFilters: true } })
          }
        />
      </Box>
      <Divider sx={{ mt: 2, mx: -5 }} />
      {isLoading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <Stack divider={<Divider sx={{ mx: -5 }} />}>
          {recentEmployees.map((emp) => (
            <EmployeeListRow
              key={emp.pkEmployeeId}
              name={emp.displayName ?? ''}
              pictureUrl={emp.pictureUrl ?? ''}
              isActive={emp.isActive ?? false}
              onClick={() =>
                navigate(`/employees?employeeId=${emp.pkEmployeeId}`)
              }
            />
          ))}
        </Stack>
      )}
    </Card>
  );
};
